import React, { useState, useEffect } from 'react';
import FileUpload from './components/FileUpload';
import FileList from './components/FileList';
import ChatInput from './components/ChatInput';
import ChatResponse from './components/ChatResponse';
import type { ChatResponseAPI, FileInfo } from './types';
import type { ApiMode } from './components/ChatInput';

interface HistoryItem {
  query:    string;
  response: ChatResponseAPI | string;
  mode:     ApiMode;
}

interface RagViewProps {
  onLogout: () => void;
}

const RagView: React.FC<RagViewProps> = ({ onLogout }) => {
  const [files,         setFiles]         = useState<FileInfo[]>([]);
  const [history,       setHistory]       = useState<HistoryItem[]>([]);
  const [showFilePanel, setShowFilePanel] = useState(false);
  const [pendingQuery,  setPendingQuery]  = useState<string | null>(null);
  const [apiMode,       setApiMode]       = useState<ApiMode>('rag');

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (history.length > 0 || files.length > 0) {
        event.preventDefault();
        event.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [history, files]);

  const handleSend = (query: string, response: ChatResponseAPI | string, mode: ApiMode) => {
    setHistory(prev => [...prev, { query, response, mode }]);
    setPendingQuery(null);
  };

  const handleModeChange = (newMode: ApiMode) => {
    if (newMode === apiMode) return;
    if (history.length > 0) {
      const ok = window.confirm(
        'Switching mode will clear the current conversation. Continue?'
      );
      if (!ok) return;
    }
    setHistory([]);
    setPendingQuery(null);
    setApiMode(newMode);
  };

  return (
    <div className="rag-dashboard">
      <div className="main-chat">
        <div className="header-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <h2 style={{ margin: 0 }}>💬 RAG Chat</h2>
            {/* ── Mode toggle ── */}
            {[
              { key: 'rag' as ApiMode,  label: 'RAG',  color: '#34d399' },
              { key: 'sql' as ApiMode,  label: 'SQL',  color: '#34d399' },
              { key: 'ai'  as ApiMode,  label: 'AI', color: '#34d399' },
            ].map((m) => (
              <button
                key={m.key}
                type="button"
                disabled={m.key === 'ai'}
                onClick={() => handleModeChange(m.key)}
                style={{
                  padding: '3px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                  cursor: m.key === 'ai' ? 'not-allowed' : 'pointer',
                  opacity: m.key === 'ai' ? 0.5 : 1,
                  border: `1.5px solid ${apiMode === m.key ? m.color : 'transparent'}`,
                  background: apiMode === m.key ? `${m.color}22` : 'rgba(255,255,255,0.05)',
                  color: apiMode === m.key ? m.color : '#94a3b8',
                  transition: 'all 0.15s',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="file-toggle-btn"
              onClick={() => setShowFilePanel(!showFilePanel)}
            >
              📁 {showFilePanel ? 'Hide' : 'Files'}
            </button>
            <button className="logout-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>

        <div className={`chat-messages ${showFilePanel ? 'shrink-chat' : ''}`}>
          <ChatResponse history={history} pendingQuery={pendingQuery} apiMode={apiMode} />
        </div>

        <div className="chat-Input-Mobile">
          <div className={`chat-input ${showFilePanel ? 'shrink' : ''}`}>
            <div className="chat-input-container">
              <ChatInput
                onSend={handleSend}
                setPendingQuery={setPendingQuery}
                lastQuery={history.length > 0 ? history[history.length - 1].query : undefined}
                apiMode={apiMode}
                onModeChange={handleModeChange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={`file-panel ${showFilePanel ? 'open' : ''}`}>
        <div className="file-panel-header">
          <h3>📁&nbsp;Upload&nbsp;Documents</h3>
          <button className="panel-close-btn" onClick={() => setShowFilePanel(false)}>×</button>
        </div>
        <div className="file-panel-content">
          <div className="upload-section">
            <FileUpload setFiles={setFiles} />
          </div>
          <div className="file-list">
            <FileList files={files} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default RagView;
